import {
  SatDiagnosticAssignmentResponse,
  SatDomain,
  SatMasteryBand,
  SatPracticeSetResponse,
  SatProfileHeroDto,
  SatProfilePhase,
  SatStudentProfileOverviewDto,
  DOMAIN_LABELS,
  SHORT_DOMAIN_LABELS,
  getPracticeSetDisplayName,
} from '@/api/sat';
import { STATUS_TONES } from '../statusScale';
import { AUTHED_PATHS, SatJourneyPaths } from './journeySource';
import type { JourneyStage } from './journeyStage';

/**
 * Student-voiced framing of the L4 read model.
 *
 * The backend serves ONE profile shape for tutors and students (by design —
 * see sat-l4-design.md §3.2). Tutors get analytical vocabulary; students get
 * motivational vocabulary. This file is the single place that translation
 * happens — if copy needs tuning, tune it here, not in components.
 */

// ── Band framing ────────────────────────────────────────────────────────────
// Honest but kind. "Developing" reads as a grade; "Focus area" reads as a plan.

export const STUDENT_BAND_LABELS: Record<SatMasteryBand, string> = {
  NOT_STARTED: 'Not started yet',
  INSUFFICIENT_EVIDENCE: 'Warming up',
  DEVELOPING: 'Focus area',
  APPROACHING: 'Getting there',
  PROFICIENT: 'Strong',
  MASTERED_CANDIDATE: 'Excellent',
};

/** Tailwind classes for skill progress bars, keyed by band. Source of truth: statusScale.ts. */
export const STUDENT_BAND_BAR_CLASSES = Object.fromEntries(
  Object.entries(STATUS_TONES).map(([band, tone]) => [band, tone.bar]),
) as Record<SatMasteryBand, string>;

/** SVG stroke classes for per-node progress rings, keyed by band. Source of truth: statusScale.ts. */
export const STUDENT_BAND_STROKE_CLASSES = Object.fromEntries(
  Object.entries(STATUS_TONES).map(([band, tone]) => [band, tone.stroke]),
) as Record<SatMasteryBand, string>;

/** Compact area names live in api/sat.ts (shared helper); re-exported for the course map. */
export { SHORT_DOMAIN_LABELS };

// ── Phase framing (hero headline) ───────────────────────────────────────────

export function heroHeadline(
  phase: SatProfilePhase,
  activeDomain: SatDomain | null,
  firstName: string | null,
  stage?: JourneyStage,
): string {
  const name = firstName ? `, ${firstName}` : '';
  switch (phase) {
    case 'NOT_STARTED':
    case 'INITIAL_DIAGNOSTIC_ASSIGNED':
    case 'INITIAL_DIAGNOSTIC_IN_PROGRESS':
      // Open mode never leaves NOT_STARTED (no L3 profile), so the stage —
      // derived from local progress — has to say where the visitor really is.
      if (stage === 'diagnosed') return "Your results are in. Here's your course.";
      if (stage === 'practicing') return 'Keep going. Your map is sharpening.';
      return `Welcome to your SAT course${name}!`;
    case 'IN_DOMAIN_WORK':
      return activeDomain
        ? `You're working on ${DOMAIN_LABELS[activeDomain]}`
        : `You're in the thick of it${name}`;
    case 'BETWEEN_DOMAINS':
      return 'Ready for your next focus area';
    case 'COMPLETED':
      return `You completed the course${name}! 🎓`;
  }
}

// ── First-run copy (specs/009 FIRST-RUN) ────────────────────────────────────
// "Free, no account" is only true for an anonymous visitor, so anything that
// says it is keyed on the journey source's mode.

type JourneyMode = 'open' | 'authed';

/** `1 activity`, `2 activities` — every count the student sees goes through this. */
export function pluralize(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export const FIRST_RUN_STEPS = [
  'Diagnostic',
  'Practice aimed at your weakest area',
  'Prove each area, one by one',
] as const;

export function firstRunEyebrow(mode: JourneyMode): string {
  return mode === 'open' ? 'FREE SAT COURSE' : 'YOUR SAT COURSE';
}

export function firstRunHeadline(stage: 'fresh' | 'diagnostic-started'): string {
  return stage === 'fresh' ? 'Your SAT course starts here.' : 'Pick up where you left off.';
}

/**
 * `anonymous` = open mode AND nobody signed in. "no account" is only true for
 * that visitor; a signed-in student on the open route (e.g. one who hasn't
 * added SAT yet) still gets "free" but never "no account".
 */
export function firstRunSub(
  stage: 'fresh' | 'diagnostic-started',
  mode: JourneyMode,
  anonymous: boolean = mode === 'open',
): string {
  const free = mode !== 'open' ? '' : anonymous ? ', free, no account' : ', free';
  return stage === 'fresh'
    ? `Begin with a short diagnostic. It builds the course around what you actually need${free}.`
    : `Finish the diagnostic and your course is built around what you actually need${free}.`;
}

export function firstRunCta(stage: 'fresh' | 'diagnostic-started'): string {
  return stage === 'fresh' ? 'Start the diagnostic' : 'Continue the diagnostic';
}

/**
 * `24 questions · about 45 min · pause anytime`, built ONLY from what the data
 * says. Question count and time limit come from the diagnostic assignment
 * (open mode has them only once the form has been fetched — before that the
 * part is omitted, never guessed). "pause anytime" is open-mode only: the open
 * timer stops while the visitor is away, but an authed attempt is timed against
 * a server `expiresAt` and keeps running.
 */
export function diagnosticMetaLine(
  assignments: SatDiagnosticAssignmentResponse[],
  mode: JourneyMode,
): string | null {
  const a = assignments.find((x) => x.status === 'IN_PROGRESS') ?? assignments.find((x) => x.status === 'ASSIGNED');
  const parts: string[] = [];
  if (a && a.questionCount > 0) parts.push(pluralize(a.questionCount, 'question', 'questions'));
  if (a && a.timeLimitMin > 0) parts.push(`about ${a.timeLimitMin} min`);
  if (mode === 'open') parts.push('pause anytime');
  if (parts.length === 0) return null;
  const line = parts.join(' · ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}

export const COURSE_PREVIEW_COPY = {
  subtitle: 'After the diagnostic these re-order, weakest first, and each ring fills as you practice.',
  fillButton: 'See it fill in',
  stopButton: 'Stop',
  /** Frame captions come from storyFrames (the one film); only the closing line is the course's own. */
  captions: {
    outro: 'Yours starts with the diagnostic.',
  },
} as const;

/**
 * First-run hero line for a visitor who answered homepage taster question(s)
 * (spec 010 WS-E). `domain` is the SatDomain enum name of the MOST RECENT answer;
 * an unknown domain (old or hand-edited storage) falls back to plain wording.
 */
export function tasterCarryLine(count: number, domain: string | null, topLevel?: string | null): string | null {
  if (!Number.isFinite(count) || count < 1) return null;
  const tail = 'The diagnostic takes it from here.';
  const label = domain ? (DOMAIN_LABELS as Record<string, string | undefined>)[domain] : undefined;
  if (count === 1) {
    return `You already answered one ${label ? `${label} ` : ''}question. ${tail}`;
  }
  // HOME-V3: the homepage ladder records how far up it the visitor got.
  const level = topLevel ? LADDER_LEVEL_LABELS[topLevel] : undefined;
  // Without a level (older stored answers) the line is the original `{n} questions`.
  if (!level) return `You already answered ${count} questions. ${tail}`;
  return `You already answered ${count} ${label ? `${label} ` : ''}questions, up to ${level}. ${tail}`;
}

/** The homepage ladder's levels (stored as the key, shown as the label). */
export const LADDER_LEVEL_LABELS: Record<string, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
  PROVE_IT: 'Prove it',
};

/** HOME-V3: the area the visitor tapped as "your weakest" on the homepage window. */
export function pickCarryLine(domain: string | null): string | null {
  const label = domain ? (DOMAIN_LABELS as Record<string, string | undefined>)[domain] : undefined;
  return label ? `You guessed ${label} is your weakest. The diagnostic checks that, and maps the other seven.` : null;
}

/**
 * The diagnostic result as one honest line: `3 of 24 correct · 11 answered`.
 *
 * Same definition as the score report — correct ÷ questions ON THE FORM, so a
 * skipped question counts as not correct and the percentage never reads higher
 * than "3 of 24" does. `fallback` fills what an older server (or the open
 * store) doesn't put on the hero. Without a question count it degrades to the
 * bare percentage rather than guessing. Null when there is no score at all.
 */
export function diagnosticSummary(
  hero: Pick<SatProfileHeroDto, 'initialDiagnosticScorePct' | 'initialDiagnosticAnsweredCount' | 'initialDiagnosticQuestionCount'>,
  fallback: { pct: number | null; questionCount: number | null } = { pct: null, questionCount: null },
): string | null {
  const pct = hero.initialDiagnosticScorePct ?? fallback.pct;
  if (pct == null) return null;
  const questions = hero.initialDiagnosticQuestionCount ?? (fallback.questionCount && fallback.questionCount > 0 ? fallback.questionCount : null);
  if (questions == null || questions <= 0) return `${Math.round(pct)}%`;
  const correct = Math.min(questions, Math.max(0, Math.round((pct / 100) * questions)));
  const answered = hero.initialDiagnosticAnsweredCount;
  return `${correct} of ${questions} correct${answered != null ? ` · ${answered} answered` : ''}`;
}

export const BASELINE_COPY = {
  later: "Later, when you're ready: a full-length practice test in Bluebook sets your baseline score.",
  whenReady: "When you're ready: set your baseline with a full practice test",
} as const;

// ── Next step resolution ────────────────────────────────────────────────────

export interface NextStep {
  headline: string;
  detail: string;
  cta: { label: string; to: string } | null;
  /** Why this step was chosen — surfaced in tests, not UI. */
  source: 'paused' | 'playable' | 'action';
}

const PLAYABLE_DIAGNOSTIC = new Set(['ASSIGNED', 'IN_PROGRESS']);
const PLAYABLE_PRACTICE = new Set(['AVAILABLE', 'IN_PROGRESS']);

/**
 * The ONE thing the student should do right now.
 *
 * Priority (run through as a user: "what's actually waiting for me?"):
 *  1. Paused course — nothing to do, say so plainly.
 *  2. Something already started (diagnostic, then practice) — finish it.
 *  3. Something assigned and unstarted — start it.
 *  4. Otherwise: translate the tutor-facing suggested action into student
 *     voice — usually a "your tutor is on it" waiting state or a results link.
 */
export function resolveNextStep(
  overview: SatStudentProfileOverviewDto,
  assignments: SatDiagnosticAssignmentResponse[],
  practiceSets: SatPracticeSetResponse[],
  paths: SatJourneyPaths = AUTHED_PATHS,
): NextStep {
  const action = overview.suggestedNextAction;

  if (overview.hero.paused || action.code === 'PAUSED') {
    return {
      headline: 'Your course is paused',
      detail:
        overview.hero.pausedReason ??
        'Nothing to do right now. Your tutor will resume it when the time is right.',
      cta: null,
      source: 'paused',
    };
  }

  // 2 + 3: playable work, started-first, diagnostics before practice.
  const playableDiagnostics = assignments
    .filter((a) => PLAYABLE_DIAGNOSTIC.has(a.status))
    .sort((a, b) => Number(b.status === 'IN_PROGRESS') - Number(a.status === 'IN_PROGRESS'));

  // Course order: everything starts with the Initial Diagnostic. Until it's
  // done, UNSTARTED practice (e.g. the auto-created reinforce set) must not
  // jump the queue — the diagnostic is the next step. A practice set the
  // student already STARTED is still finishable.
  // `hero.phase` alone isn't enough: open mode has no L3 profile, so its phase
  // never leaves NOT_STARTED. A completed diagnostic assignment (which open mode
  // synthesizes from local progress) or a stamped completion time also counts.
  const initialDone =
    !(
      overview.hero.phase === 'NOT_STARTED' ||
      overview.hero.phase === 'INITIAL_DIAGNOSTIC_ASSIGNED' ||
      overview.hero.phase === 'INITIAL_DIAGNOSTIC_IN_PROGRESS'
    ) ||
    overview.hero.initialDiagnosticCompletedAt != null ||
    assignments.some((a) => a.status === 'COMPLETED');
  const playablePractice = practiceSets
    .filter((p) => PLAYABLE_PRACTICE.has(p.status))
    .filter((p) => initialDone || p.status === 'IN_PROGRESS')
    .sort((a, b) => Number(b.status === 'IN_PROGRESS') - Number(a.status === 'IN_PROGRESS'));

  const inProgressDiagnostic = playableDiagnostics.find((a) => a.status === 'IN_PROGRESS');
  const inProgressPractice = playablePractice.find((p) => p.status === 'IN_PROGRESS');

  if (inProgressDiagnostic) {
    return {
      headline: `Finish your ${inProgressDiagnostic.templateName}`,
      detail: 'You already started. Pick up right where you left off.',
      cta: { label: 'Continue', to: paths.diagnostic(inProgressDiagnostic.id) },
      source: 'playable',
    };
  }
  if (inProgressPractice) {
    return {
      headline: `Finish your ${getPracticeSetDisplayName(inProgressPractice.name)}`,
      detail: 'You already started. Pick up right where you left off.',
      cta: { label: 'Continue', to: paths.practice(inProgressPractice.id) },
      source: 'playable',
    };
  }

  const assignedDiagnostic = playableDiagnostics[0];
  if (assignedDiagnostic) {
    const isInitial = action.code === 'INITIAL_DIAGNOSTIC_NEEDED'
      || action.code === 'STUDENT_TO_COMPLETE_INITIAL'
      || overview.hero.phase === 'NOT_STARTED'
      || overview.hero.phase === 'INITIAL_DIAGNOSTIC_ASSIGNED';
    return {
      headline: `Start your ${assignedDiagnostic.templateName}`,
      detail: isInitial
        ? 'This maps your strengths so every practice set targets exactly what you need. Find a quiet moment and give it your real effort.'
        : 'A quick check-in that shows which skills to sharpen next.',
      cta: { label: 'Start now', to: paths.diagnostic(assignedDiagnostic.id) },
      source: 'playable',
    };
  }

  const availablePractice = playablePractice[0];
  if (availablePractice) {
    const target = availablePractice.skillName ?? (availablePractice.domain ? DOMAIN_LABELS[availablePractice.domain] : null);
    return {
      headline: `Practice is ready: ${getPracticeSetDisplayName(availablePractice.name)}`,
      detail: target
        ? `${pluralize(availablePractice.questionCount, 'question', 'questions')} on ${target}. Consistent reps are what raise scores.`
        : `${pluralize(availablePractice.questionCount, 'question', 'questions')} picked for you. Consistent reps are what raise scores.`,
      cta: { label: 'Start practice', to: paths.practice(availablePractice.id) },
      source: 'playable',
    };
  }

  // 4: nothing playable — student-voiced translation of the suggested action.
  return { ...actionCopy(overview, paths), source: 'action' };
}

function actionCopy(
  overview: SatStudentProfileOverviewDto,
  paths: SatJourneyPaths,
): Omit<NextStep, 'source'> {
  const action = overview.suggestedNextAction;
  const domainLabel = action.domain ? DOMAIN_LABELS[action.domain] : null;

  switch (action.code) {
    case 'INITIAL_DIAGNOSTIC_NEEDED':
      return {
        headline: 'Your journey starts with a diagnostic',
        detail:
          'It maps your strengths across all 8 areas so your practice targets exactly what you need. Your tutor is setting it up. Check back soon.',
        cta: null,
      };
    case 'STUDENT_TO_COMPLETE_INITIAL':
      return {
        headline: 'Your diagnostic is waiting',
        detail:
          'Find a quiet ~40 minutes and give it your real effort. It shapes your whole course.',
        cta: action.assignmentId
          ? { label: 'Open diagnostic', to: paths.diagnostic(action.assignmentId) }
          : null,
      };
    case 'REVIEW_INITIAL_DIAGNOSTIC':
      return {
        headline: 'Your results are in! 🎉',
        detail:
          'Your skill map below just lit up. Your tutor is picking your first focus area. Meanwhile, see how you did.',
        cta: action.assignmentId
          ? { label: 'See my results', to: paths.diagnosticResult(action.assignmentId) }
          : null,
      };
    case 'SELECT_ACTIVE_DOMAIN':
      return {
        headline: 'Choosing your next focus area',
        detail:
          'Your tutor is picking the area where practice will move your score the fastest.',
        cta: null,
      };
    case 'ASSIGN_DOMAIN_DIAGNOSTIC':
      return {
        headline: 'Up next: a quick check-in quiz',
        detail: domainLabel
          ? `A short quiz in ${domainLabel} shows exactly which skills to sharpen first. It's on its way.`
          : 'A short quiz shows exactly which skills to sharpen first. It’s on its way.',
        cta: null,
      };
    case 'SELECT_ACTIVE_SKILL':
      return {
        headline: domainLabel ? `Zooming in on ${domainLabel}` : 'Zooming in',
        detail: 'Your tutor is picking the exact skill to practice next.',
        cta: null,
      };
    case 'CONTINUE_SKILL_PRACTICE':
      return {
        headline: action.skillName
          ? `Keep practicing ${action.skillName}`
          : 'Keep practicing',
        detail:
          'Consistent reps are what raise scores. Your next set will show up here the moment it’s ready.',
        cta: null,
      };
    case 'ASSIGN_MASTERY_CHECK':
      return {
        headline: 'You’re ready to prove it 💪',
        detail: domainLabel
          ? `You’ve put in the work on ${domainLabel}, and a mastery check is coming to make it official.`
          : 'You’ve put in the work, and a mastery check is coming to make it official.',
        cta: null,
      };
    case 'REVIEW_MASTERY_CHECK':
      return {
        headline: 'Mastery check complete',
        detail: 'Your tutor is reviewing it. Watch your map for the verdict.',
        cta: null,
      };
    case 'REVIEW_PREVIOUSLY_MASTERED_SKILL':
      return {
        headline: 'Time for a quick refresher',
        detail:
          'Something you mastered earlier could use a touch-up. Your tutor is lining up a review.',
        cta: null,
      };
    case 'COLLECT_MORE_EVIDENCE':
      return {
        headline: 'Keep going!',
        detail: 'A few more questions and your skill map gets much sharper.',
        cta: null,
      };
    case 'JOURNEY_COMPLETE':
      return {
        headline: 'You did it! 🎓',
        detail: 'Every area mastered. You’re ready for test day.',
        cta: null,
      };
    case 'PAUSED': // handled earlier; here for exhaustiveness
      return {
        headline: 'Your course is paused',
        detail: 'Your tutor will resume it when the time is right.',
        cta: null,
      };
    case 'TUTOR_REVIEW_NEEDED':
    case 'MANUAL_REVIEW_NEEDED':
    default:
      return {
        headline: 'Your tutor is reviewing your progress',
        detail: 'Nothing for you right now. Check back soon.',
        cta: null,
      };
  }
}
